"use client";

import { useTranslations } from "next-intl";

interface Props {
  premiseKey: string;
  elapsedMs: number;
  /** Squares decided, not taps: cycling one square costs a single click. */
  clicks: number;
  onUndo: () => void;
  canUndo: boolean;
  onClear: () => void;
  onHint: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  hintsRevealed: number;
}

export function Toolbar({
  premiseKey,
  elapsedMs,
  clicks,
  onUndo,
  canUndo,
  onClear,
  onHint,
  onSubmit,
  canSubmit,
  hintsRevealed,
}: Props) {
  const t = useTranslations("game");
  const premise = useTranslations(`premises.${premiseKey}`);

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
      <button type="button" data-testid="undo" onClick={onUndo} disabled={!canUndo} style={buttonStyle}>
        {t("undo")}
      </button>
      <button type="button" data-testid="clear" onClick={onClear} style={buttonStyle}>
        {t("clear")}
      </button>
      <button type="button" data-testid="hint" onClick={onHint} style={buttonStyle}>
        {t("hint")}
        {hintsRevealed > 0 ? ` (${hintsRevealed})` : ""}
      </button>

      <span style={{ marginLeft: "auto", display: "flex", gap: "0.9rem", color: "var(--muted)" }}>
        <span data-testid="clicks" title={t("clicksHint")} style={numberStyle}>
          {t("clicks", { count: clicks })}
        </span>
        <span data-testid="timer" style={numberStyle}>
          {formatTime(elapsedMs)}
        </span>
      </span>

      <button
        type="button"
        data-testid="submit"
        onClick={onSubmit}
        disabled={!canSubmit}
        title={canSubmit ? premise("accuseReady") : t("accuseDisabled")}
        style={{ ...buttonStyle, ...(canSubmit ? primaryStyle : {}) }}
      >
        {t("accuse")}
      </button>
    </div>
  );
}

function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

// Longhand border properties, not the `border` shorthand: the accuse button swaps between this and
// primaryStyle, and mixing a shorthand here with a longhand there makes React drop one of them on
// the way back — it warns about exactly this, and the button would keep the accent border after it
// stopped being primary.
const buttonStyle = {
  padding: "0.55rem 0.85rem",
  fontSize: "0.95rem",
  borderRadius: "0.45rem",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "rgba(128,128,128,0.45)",
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  minHeight: "2.75rem",
} as const;

const numberStyle = { fontVariantNumeric: "tabular-nums" } as const;

const primaryStyle = {
  background: "var(--accent)",
  borderColor: "var(--accent)",
  color: "#fff",
  fontWeight: 600,
} as const;
